
































































import { Component, Ref, Vue, Watch } from "vue-property-decorator";
import BaseDialogComponent from "@/components/common/BaseDialog.vue";
import ConfirmationDialogComponent from "@/components/common/ConfirmationDialog.vue";
import { ClientStatus, ClientStatusFlags, DELETE_REASONS, DeleteReason } from "@/data/models/ClientAdministrationRow";
import { setClientStatus } from "@/data/repository/ClientAdministrationRepository";
import { SnackbarModule } from "@/store/modules/SnackbarModule";
import {
  STATUS_NOTE_MAX_LENGTH,
  StatusCondition,
  StatusDialogMode,
  StatusDialogState,
  canSaveStatus,
  deleteReasonLabel,
  hasStatusChanged,
  initialChoice,
  noteToSend,
  statusActionOf,
  statusConditionKey,
  statusRefusalOf,
  takesNote,
} from "@/utils/ClientStatus";

/** What `show()` resolves with: the saved Status, CHANGED_MEANWHILE (the caller reloads), or null. */
export type StatusDialogResult = ClientStatus | "CHANGED_MEANWHILE" | null;

const NO_FLAGS: ClientStatusFlags = { blocked: false, isActive: true, deleted: false };

/**
 * Changes one flag of a client's Status (#2610067): Gesperrt with an optional note, Aktiv / Inaktiv,
 * Löschen with a reason, Wiederherstellen. IRES Cloud checks the conditions; a refusal lists what stands
 * in the way and leaves the dialog open.
 */
@Component({ components: { "base-dialog": BaseDialogComponent, "confirmation-dialog": ConfirmationDialogComponent } })
export default class ClientStatusDialogComponent extends Vue {
  @Ref("baseDialog")
  baseDialog!: BaseDialogComponent;

  @Ref("confirm")
  confirmDialog!: ConfirmationDialogComponent;

  readonly noteMaxLength = STATUS_NOTE_MAX_LENGTH;

  busy = false;
  clientId = "";
  // Initialised, not `!`: the computeds below must track them from the first render (edit-mode-dialogs.md).
  mode: StatusDialogMode = "blocked";
  current: ClientStatusFlags = { ...NO_FLAGS };
  choice = false;
  // null, not undefined: an undefined initializer is not reactive in vue-class-component.
  reason: DeleteReason | null = null;
  note = "";
  unmetConditions: StatusCondition[] = [];

  private resolve: ((result: StatusDialogResult) => void) | null = null;

  get state(): StatusDialogState {
    return { mode: this.mode, current: this.current, choice: this.choice, reason: this.reason, note: this.note };
  }

  get hasChanged(): boolean {
    return hasStatusChanged(this.state);
  }

  get canSave(): boolean {
    return canSaveStatus(this.state);
  }

  get noteApplies(): boolean {
    return takesNote(this.state);
  }

  get noteLabelKey(): string {
    return this.mode === "delete" && this.reason === "OTHER" ? "admin.clients.status.noteRequired" : "admin.clients.status.note";
  }

  get reasonItems(): Array<{ value: DeleteReason; label: string }> {
    return DELETE_REASONS.map((value) => ({ value, label: deleteReasonLabel(value, (key) => this.$t(key).toString()) }));
  }

  /** What the change does, said before it is made. */
  get consequenceKey(): string | null {
    if (this.mode === "blocked") {
      return this.choice && !this.current.blocked ? "admin.clients.status.blocked.consequence" : null;
    }
    if (this.mode === "active") {
      return !this.choice && this.current.isActive ? "admin.clients.status.active.consequence" : null;
    }
    return this.mode === "delete" ? "admin.clients.status.delete.consequence" : null;
  }

  get unmetTitleKey(): string {
    return this.mode === "delete" ? "admin.clients.status.delete.unmet" : "admin.clients.status.active.unmet";
  }

  noteRule(value: string): boolean | string {
    return (value ?? "").trim().length <= STATUS_NOTE_MAX_LENGTH || this.$t("admin.clients.status.noteTooLong").toString();
  }

  conditionKey(condition: StatusCondition): string {
    return statusConditionKey(condition);
  }

  /**
   * @param mode - what to change.
   * @param clientId - the client.
   * @param current - its flags as the detail shows them.
   */
  show(mode: StatusDialogMode, clientId: string, current: ClientStatusFlags): Promise<StatusDialogResult> {
    this.mode = mode;
    this.clientId = clientId;
    this.current = { ...current };
    this.choice = initialChoice(mode, current);
    this.reason = null;
    this.note = "";
    this.unmetConditions = [];
    this.baseDialog.show({
      title: this.$t(`admin.clients.status.${mode}.title`).toString(),
      positiveButtonText: this.$t(this.positiveButtonKey(mode)).toString(),
      negativeButtonText: this.$t("common.close").toString(),
      maxWidth: 560,
      persistent: true,
      closeOnSave: false,
    });
    return new Promise((resolve) => (this.resolve = resolve));
  }

  private positiveButtonKey(mode: StatusDialogMode): string {
    if (mode === "delete") {
      return "common.delete";
    }
    return mode === "restore" ? "admin.clients.status.restore.button" : "common.save";
  }

  /** SCHLIEßEN while nothing changed, ABBRECHEN after (edit-mode-dialogs.md). */
  @Watch("hasChanged")
  onHasChanged(hasChanged: boolean) {
    this.baseDialog.negativeButtonText = this.$t(hasChanged ? "common.abort" : "common.close").toString();
  }

  /** A refusal's list belongs to the request that caused it, not to the next one. */
  @Watch("choice")
  @Watch("reason")
  onInputChanged() {
    this.unmetConditions = [];
  }

  /** SCHLIEßEN has already closed the dialog; ABBRECHEN asks first. */
  async onNegative() {
    if (!this.hasChanged) {
      this.finish(null);
      return;
    }
    const discard = await this.confirmDialog.show({
      title: this.$t("common.confirmationDialog.unsavedChangesTitle").toString(),
      message: this.$t("common.confirmationDialog.unsavedChangesMessage").toString(),
      acceptMessage: this.$t("common.abort").toString(),
      rejectMessage: this.$t("common.back").toString(),
    });
    if (discard) {
      this.close(null);
    }
  }

  async onSave() {
    if (!this.canSave) {
      return;
    }
    this.busy = true;
    this.unmetConditions = [];
    try {
      const saved = await setClientStatus(
        this.clientId,
        statusActionOf(this.state),
        this.current,
        this.mode === "delete" ? this.reason : null,
        noteToSend(this.state)
      );
      SnackbarModule.showSuccess({ key: `admin.clients.status.${this.mode}.saved` });
      this.close(saved ?? "CHANGED_MEANWHILE");
    } catch (error) {
      const refusal = statusRefusalOf(error);
      if (refusal.kind === "CONDITIONS_NOT_MET") {
        // Stays open: the list says what to clear first.
        this.unmetConditions = refusal.conditions;
      } else if (refusal.kind === "CHANGED_MEANWHILE") {
        SnackbarModule.showError({ key: "admin.clients.status.changedMeanwhile" });
        this.close("CHANGED_MEANWHILE");
      } else {
        SnackbarModule.showError({ key: "admin.clients.status.saveFailed" });
      }
    } finally {
      this.busy = false;
    }
  }

  close(result: StatusDialogResult) {
    this.baseDialog.isShowing = false;
    this.finish(result);
  }

  private finish(result: StatusDialogResult) {
    this.resolve?.(result);
    this.resolve = null;
  }
}
